import { useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowUpLeft, Building2, Loader2 } from 'lucide-react';
import { apiEndpoints, apiRequest, resolveApiAssetUrl } from '@/config/api';
import type { ApiResponse, Customer } from '@/types/api';

const fallbackCustomers: Customer[] = [];

const featuredCustomerOrder = [
  'کارخانه هود کن', 'شرکت اسنوا', 'شرکت اخوان', 'شرکت استیل البرز', 'سیماران',
  'شرکت داتیس', 'ایلیا استیل', 'شرکت تهران بالا', 'شرکت تهویه آرسام', 'خزر الکتریک', 'شرکت ایمان خزر',
];

const getPriority = (name: string) => {
  const index = featuredCustomerOrder.findIndex((featuredName) => name.includes(featuredName) || featuredName.includes(name));
  return index === -1 ? featuredCustomerOrder.length : index;
};

const localCustomerLogos: Record<string, string> = {
  'سیماران': '/images/customers/simaran.png',
  'اسنوا': '/images/customers/snowa.png',
  'اخوان': '/images/customers/akhavan.png',
  'استیل البرز': '/images/customers/steel-alborz.png',
  'گروه صنعتی CAN': '/images/customers/can.png',
};

const getCustomerLogo = (customer: Customer) => {
  if (customer.logo) return resolveApiAssetUrl(customer.logo);
  const match = Object.entries(localCustomerLogos).find(([name]) => customer.name.includes(name));
  return match?.[1] || null;
};

const getInitials = (name: string) => {
  const words = name.replace(/^(شرکت|کارخانه|گروه صنعتی|گروه)\s*/u, '').trim().split(/\s+/u).filter(Boolean);
  return (words.slice(0, 2).map((word) => word[0]).join('') || 'ک').slice(0, 2);
};

const CustomerItem = ({ customer }: { customer: Customer }) => {
  const logo = getCustomerLogo(customer);
  const content = (
    <div className="group flex h-[80px] min-w-[220px] shrink-0 items-center gap-3 rounded-2xl border border-border/70 bg-card/90 px-4 text-right shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-copper/50 hover:bg-card hover:shadow-lg hover:shadow-copper/10">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border/60 bg-muted/40 text-sm font-black text-muted-foreground transition-colors duration-300 group-hover:border-copper/30 group-hover:bg-copper/10 group-hover:text-copper">
        {logo ? <img src={logo} alt={`لوگوی ${customer.name}`} width={48} height={48} loading="lazy" draggable={false} className="h-full w-full object-contain p-1.5 grayscale opacity-60 saturate-0 transition duration-300 group-hover:grayscale-0 group-hover:opacity-100 group-hover:saturate-100" /> : <span className="transition-colors duration-300 group-hover:text-copper">{getInitials(customer.name)}</span>}
      </div>
      <div className="min-w-0">
        <p className="line-clamp-2 text-sm font-bold leading-6 text-foreground transition-colors duration-300 group-hover:text-copper">{customer.name}</p>
        {customer.website && <span className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-muted-foreground">وب‌سایت <ArrowUpLeft className="h-3 w-3" /></span>}
      </div>
    </div>
  );
  return customer.website ? <a href={customer.website} target="_blank" rel="noreferrer" aria-label={`مشاهده وب‌سایت ${customer.name}`}>{content}</a> : content;
};

const CustomersSection = () => {
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ pointerId: number; startX: number; startY: number; startScrollLeft: number; axis: 'x' | 'y' | null } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['customers'],
    queryFn: () => apiRequest<ApiResponse<Customer[]>>(`${apiEndpoints.customers}?active=true`),
    staleTime: 60_000,
  });

  const customers = useMemo(() => (data?.data?.length ? data.data : fallbackCustomers).slice().sort((a, b) => Number(Boolean(getCustomerLogo(b))) - Number(Boolean(getCustomerLogo(a))) || getPriority(a.name) - getPriority(b.name) || a.sort_order - b.sort_order || a.id - b.id), [data]);
  const marqueeCustomers = useMemo(() => [...customers, ...customers], [customers]);

  useEffect(() => {
    if (isPaused || isDragging || customers.length < 2) return;
    let animationFrame = 0;
    const tick = () => {
      const viewport = viewportRef.current;
      if (viewport && viewport.scrollWidth > viewport.clientWidth) {
        viewport.scrollLeft += 0.55;
        const loopPoint = viewport.scrollWidth / 2;
        if (viewport.scrollLeft >= loopPoint) viewport.scrollLeft -= loopPoint;
      }
      animationFrame = window.requestAnimationFrame(tick);
    };
    animationFrame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [customers.length, isDragging, isPaused]);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const viewport = viewportRef.current;
    if (!viewport) return;
    dragRef.current = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, startScrollLeft: viewport.scrollLeft, axis: null };
    try { viewport.setPointerCapture(event.pointerId); } catch { /* Pointer capture is unavailable in a few older browsers. */ }
    setIsPaused(true);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const viewport = viewportRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !viewport) return;
    const distanceX = event.clientX - drag.startX;
    const distanceY = event.clientY - drag.startY;
    if (!drag.axis && (Math.abs(distanceX) > 8 || Math.abs(distanceY) > 8)) drag.axis = Math.abs(distanceX) > Math.abs(distanceY) ? 'x' : 'y';
    if (drag.axis !== 'x') return;
    if (Math.abs(distanceX) > 8) {
      setIsDragging(true);
      event.preventDefault();
      viewport.scrollLeft = drag.startScrollLeft - distanceX;
    }
  };

  const handlePointerEnd = (event: PointerEvent<HTMLDivElement>) => {
    const viewport = viewportRef.current;
    if (viewport?.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
    dragRef.current = null;
    setIsDragging(false);
    setIsPaused(false);
  };

  return (
    <section id="customers" dir="rtl" className="relative overflow-hidden bg-background py-12 md:py-16">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="text-right">
            <div className="mb-2 inline-flex items-center gap-2 text-sm font-bold text-copper"><Building2 className="h-4 w-4" />اعتماد حاصل همکاری</div>
            <h2 className="text-3xl font-black tracking-tight text-foreground md:text-4xl">مشتریان ما</h2>
          </div>
          <p className="max-w-xl text-right text-sm leading-7 text-muted-foreground md:text-left">مجموعه‌هایی که در مسیر تولید و تأمین محصولات کیا الکتروموتور با ما همراه بوده‌اند.</p>
        </div>

        {isLoading && !data ? <div className="flex h-[112px] items-center justify-center rounded-3xl border border-border bg-muted/30"><Loader2 className="h-7 w-7 animate-spin text-copper" /></div> : customers.length === 0 ? <div className="rounded-3xl border border-dashed border-border p-8 text-center text-muted-foreground">هنوز مشتری‌ای برای نمایش ثبت نشده است.</div> : (
          <div className="relative overflow-hidden rounded-3xl border border-border/70 bg-muted/30 py-4 shadow-sm">
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-20 bg-gradient-to-r from-background to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-20 bg-gradient-to-l from-background to-transparent" />
            <div ref={viewportRef} dir="ltr" onPointerDownCapture={handlePointerDown} onPointerMoveCapture={handlePointerMove} onPointerUpCapture={handlePointerEnd} onPointerCancelCapture={handlePointerEnd} onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => { if (!dragRef.current) setIsPaused(false); }} className={`scrollbar-none flex gap-3 overflow-x-auto px-4 [touch-action:pan-y] ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`} aria-label="فهرست افقی مشتریان">
              {marqueeCustomers.map((customer, index) => <CustomerItem key={`${customer.id}-${index}`} customer={customer} />)}
            </div>
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2 text-xs text-muted-foreground"><ArrowLeft className="h-3.5 w-3.5 text-copper" /> حرکت خودکار به چپ؛ برای توقف نشانگر را روی لوگوها ببرید</div>
      </div>
    </section>
  );
};

export default CustomersSection;
